import { getVehicleString, visualizeVehicle } from '../_auto-sync/autoSyncVisualizer.js';

import {
  onVehicleSelected as cartVerifyOnVehicleSelected,
  onVehicleDiscarded as cartVerifyOnVehicleDiscarded,
  AfterInit as cartVerifyAfterInit,
} from './cartPageVerifyFitment.js';

import {
  onVehicleSelected as positionCompatibilityOnVehicleSelected,
  onVehicleDiscarded as positionCompatibilityOnVehicleDiscarded,
  AfterInit as positionCompatibilityAfterInit,
} from './positionCompatibility.js';

const AutoSyncKey = 'factoryreproductions';

const baseFitmentFields = ['Year', 'Make', 'Model', 'Submodel', 'Drive'];
const extraFitmentFields = [];
const wheelsFields = [
  'product_wheel_diameter',
  'product_wheel_width',
  'wheel_diameter',
  'wheel_width',
  'wheel_bolt_pattern',
];
const ignoreFields = [...wheelsFields];

const categorySelectionPageUrl = '/categories/';
const dealerPageUrl = '/dealer/';

export const isCartPage = window.location.pathname.includes('/cart');
export const isProductPage = window.location.pathname.includes('/product/');
const isDealerPage = window.location.pathname.includes(dealerPageUrl);
const isCheckoutPage = window.location.pathname.includes('/checkout/');
const vehicleLanding = window.document
  .querySelector('.product-archive[vehicle-landing]')
  ?.getAttribute('vehicle-landing');

// because Vehicle field on landings is sending from the Client, we need to hide it manually from chips
if (vehicleLanding) {
  ignoreFields.push('Vehicle');
}

// dealer_discount_percent is a global JS const declared inside the script tag, not a window variable
window.Convermax.dealerDiscount =
  // eslint-disable-next-line camelcase, no-undef
  typeof dealer_discount_percent !== 'undefined' ? dealer_discount_percent : null;

window.Convermax.handleProductQtySelector = (productId, isPlus) => {
  const qtyInput = window.document.querySelector(`#quantity_${productId}`);
  const addToCartButtons = window.document.querySelectorAll(
    `.add_to_cart_button[data-product_id="${productId}"]`,
  );

  if (qtyInput) {
    if (isPlus) {
      qtyInput.value++;
    } else {
      qtyInput.value--;
    }

    addToCartButtons.forEach((el) => {
      el.dataset.quantity = qtyInput.value;
    });
  }
};

window.Convermax.verifyFitmentPositionAsync = function verifyFitmentPositionAsync(variantId) {
  // 'facet.0.field=Year&facet.0.selection=2014&facet.1.field=Make&facet.1.selection=Ford&facet.2.field=Model&facet.2.selection=F-150'
  const requestFacets = window.Convermax.getVehicleRequestParams();

  if (!requestFacets) {
    return 'Please, select a vehicle first.';
  }

  const url = `https://factoryreproductions.myconvermax.com/search.json?filterquery=product_id%3A${variantId}&pagesize=1&mode=VerifyFitment&${requestFacets}&variantsMode=Strip`;
  return window
    .fetch(url, { method: 'GET' })
    .then((response) => {
      if (response?.ok) {
        return response.text();
      }

      throw new Error('Something went wrong. If this error persists, please notify Convermax.');
    })
    .then((responseText) => {
      const response = JSON.parse(responseText);

      const [item] = response.Items;

      if (!item) {
        throw new Error(
          `No fitments found with id: '${variantId}'. Please check if variant id is correct or start reindexing.`,
        );
      }

      try {
        handleAutoSyncBannerOnVerify(response);
      } catch (error) {
        console.error(error);
      }

      return { fits: item.fitsTheVehicle, positionCompatibility: item.position_compatibility };
    });
};

function getLocalPreselection(pageType) {
  const basePreselection = { field: 'product_item', term: isDealerPage ? 'False' : 'True' };

  if (vehicleLanding) {
    return [{ field: 'Vehicle', term: vehicleLanding }, basePreselection];
  }

  if (pageType === 'tag') {
    // /product-tag/r15-seeker
    return [
      { field: 'tag_slugs', term: window.location.pathname.replace('/product-tag/', '') },
      basePreselection,
    ];
  }

  if (pageType === 'category') {
    return [
      {
        field: 'categories',
        term: window.cm_category,
        treeLevel: window.cm_category.length - 1,
      },
      basePreselection,
    ];
  }

  return [basePreselection];
}

const onVehicleSelected = (vehicle) => {
  cartVerifyOnVehicleSelected(vehicle);
  positionCompatibilityOnVehicleSelected();
};

const onVehicleDiscarded = () => {
  window.document.querySelector('#cm_on-your-ride-title')?.classList.add('cm_hide');
  window.document.querySelector('#cm_on-your-ride-inner')?.remove();

  cartVerifyOnVehicleDiscarded();
  positionCompatibilityOnVehicleDiscarded();
};

function handleAutoSyncBannerOnVerify(response) {
  if (!isProductPage) {
    return;
  }

  const autoSyncBanner = window.document.querySelector('#cm_on-your-ride');
  const autoSyncButton = window.document.querySelector(
    '.cm_vehicle-widget__verify-fitment .cm_view-on-vehicle-btn',
  );

  const fits = response.Items?.[0]?.fitsTheVehicle;
  const wheelMpn = response.Items?.[0]?.wheel_mpn;
  // the vehicle is not stored in the state yet, so we can't use window.Convermax.getVehicle() at the moment
  const vehicleString = getVehicleStringFromResponse(response);

  window.Convermax.currentWheelMpn = wheelMpn;

  if (fits === 'yes' && vehicleString && wheelMpn) {
    autoSyncBanner?.classList.remove('cm_hide');
    autoSyncButton?.classList.remove('cm_hide');
    updateVisualizationPDP(vehicleString);
  } else {
    autoSyncBanner?.classList.add('cm_hide');
    autoSyncButton?.classList.add('cm_hide');
    window.document.querySelector('#cm_on-your-ride-inner')?.remove();
  }
}

function getVehicleStringFromResponse(response) {
  const getVehicleValue = (field) => {
    return response.Facets?.find((f) => f.FieldName === field)?.Selection?.[0]?.Value;
  };

  const vehicleValues = ['Year', 'Make', 'Model', 'Submodel']
    .map((field) => getVehicleValue(field))
    .filter(Boolean);

  return vehicleValues.length >= 3 ? vehicleValues.join(' ') : null;
}

function updateVisualizationPDP(vehicleString) {
  const autoSyncBanner = window.document.querySelector('#cm_on-your-ride');
  if (!autoSyncBanner) {
    return;
  }

  const wheelMpn = window.Convermax.currentWheelMpn;

  if (!vehicleString || !wheelMpn) {
    return;
  }

  window.document.querySelector('#cm_on-your-ride-inner')?.remove();

  const innerDiv = window.document.createElement('div');
  innerDiv.id = 'cm_on-your-ride-inner';
  autoSyncBanner.appendChild(innerDiv);

  setTimeout(() => {
    try {
      visualizeVehicle({
        apiKey: AutoSyncKey,
        autoSyncConfig: {
          elId: 'cm_on-your-ride-inner',
          height: '400px',
          vehicleImageSize: 640,
          widthAdjustment: 'scale',
        },
        vehicleString,
        wheelMpn,
      });
    } catch (error) {
      if (error.name !== 'NotSupportedError') {
        console.error('Failed to visualize vehicle', error);
      }
    }
  }, 0);
}

const onCartCrossSellRendered = () => {
  if (!window.Convermax.isVehicleSelected()) {
    return;
  }

  const crossSellContainer = window.document.querySelector('.cm_cart-cross-sell_container');

  if (!crossSellContainer) {
    return;
  }

  const cartItemsContainer = window.document.querySelector('.woocommerce-cart-form__contents tbody');
  const updateButton = cartItemsContainer.querySelector('tr:not(.woocommerce-cart-form__cart-item)');

  const title = crossSellContainer.querySelector('.cm_cart-cross-sell_title');
  cartItemsContainer.insertBefore(title, updateButton);

  const items = crossSellContainer.querySelectorAll('.cm_cart-cross-sell_item');
  items.forEach((node) => {
    cartItemsContainer.insertBefore(node, updateButton);
  });
};

const AfterInit = () => {
  if (isCheckoutPage) {
    const hiddenInput = window.document.querySelector('#reika_hidden_checkout_field');
    const vehicleString = getVehicleString();

    if (hiddenInput && vehicleString) {
      hiddenInput.value = vehicleString;
    }
  }

  cartVerifyAfterInit();
  positionCompatibilityAfterInit();
};

export default {
  platform: 'woocommerce',
  searchPageUrl: '/shop/',
  AfterInit,
  getLocalPreselection,
  page: {
    getPageType(defaults) {
      return window.location.pathname.includes('/product-category/')
        ? 'category'
        : window.location.pathname.includes('/product-tag/')
          ? 'tag'
          : defaults.getter();
    },
  },
  SearchRequestDefaults: {
    pageSize: 36,
  },
  product: {
    noImageSrc: '/wp-content/uploads/woocommerce-placeholder-1000x1000.png',
  },
  facets: {
    rangedFacet: [{ fields: ['wheel_bore'], step: '1/10//1', slider: { mode: 'LowerBound' } }],
  },
  fitmentSearch: {
    baseFields: baseFitmentFields,
    extraFields: extraFitmentFields,
    categorySelectionPages: [{ pathname: categorySelectionPageUrl, field: 'categories' }],
    onVehicleSelected,
    onVehicleDiscarded,
    onVerifyFitmentInitResponseReceived: handleAutoSyncBannerOnVerify,
    onVerifyFitmentResponseReceived: handleAutoSyncBannerOnVerify,
  },
  Widgets: [
    {
      name: 'SearchPage',
      location: '#cm_results',
      template: 'MainContent',
    },
    {
      name: 'DealerSearchPage',
      type: 'SearchPage',
      location: '#cm_dealer-results',
      template: 'dealerSearch/SearchPage',
    },
    {
      name: 'SearchHeader',
      sortSelectClass: 'form-select form-select--small',
      sortEntries: {
        'relevance': 'Best Match',
        'name': 'A to Z',
        'name:desc': 'Z to A',
        'price': 'Price: Ascending',
        'price:desc': 'Price: Descending',
      },
      pageSizes: [48, 9, 12, 18, 24],
    },
    {
      name: 'SearchResult',
    },
    {
      name: 'FacetPanel',
      type: 'SearchPage',
      location: '#cm_facets',
      template: 'FacetPanelContainer',
      ignoreFields,
    },
    {
      name: 'FacetBarWheels',
      type: 'FacetBar',
      fields: wheelsFields,
    },
    {
      name: 'FilterChips',
      type: 'FacetPanel',
      ignoreFields,
    },
    {
      name: 'WheelModelFacet',
      type: 'FacetPanel',
      fields: ['Wheel Model'],
    },
    {
      name: 'Facets',
      type: 'FacetPanel',
      ignoreFields: [...ignoreFields, 'Wheel Model'],
    },
    {
      name: 'FacetCategoryTiles',
      type: 'FacetTiles',
      location: '#cm_categories',
      template: 'facetTiles/hierarchicalContainer',
    },
    {
      name: 'SearchVehicleWidget',
      type: 'VehicleWidget',
    },
    {
      name: 'SearchBox',
      location: {
        replace: '#cm_search-box-icon .elementor-search-form__input',
        class: 'cm_search-box-root_container',
      },
      template: 'SearchBox',
    },
    {
      name: 'SearchBoxDialogButton',
      location: {
        replace: '#cm_search-box-icon-mobile .elementor-search-form__toggle',
        class: 'cm_desktop-hide',
      },
      template: 'searchBox/dialogButtonMobile',
    },
    {
      name: 'DealerSearchBox',
      type: 'SearchBox',
      disableDropdown: true,
      redirectUrl: dealerPageUrl,
    },
    {
      name: 'HeaderVehicleWidget_cart',
      type: 'VehicleWidget',
      location: {
        firstChildOf: '.e-cart__column-end .e-cart__column-inner',
      },
      template: 'fitmentSearch/cartVehicleWidget',
      isAlwaysActive: false,
      isAlwaysColumnLayout: true,
      isAutoVehicleSelectionDisabled: true,
      doNotRedirectOnVehicleSelect: true,
    },
    {
      name: 'SingleVehicleGarage',
      location: { selector: '#cm_garage', class: 'cm_garage-container' },
      template: 'fitmentSearch/singleVehicleGarage',
    },
    {
      name: 'VerifyFitment',
      location: {
        insertBefore: '.elementor-widget-custom_staggered_add_to_cart',
        class: 'cm_vehicle-widget__verify-fitment-container',
      },
      template: 'fitmentSearch/verifyFitment',
    },
    {
      name: 'RelatedItems',
      location: '#cm_related-items',
      template: 'product/relatedItems',
      count: 4,
      requestExtra: { wheelsOnly: true },
      useManualItems: true,
    },
    {
      name: 'WheelChart',
      type: 'RelatedItems',
      location: '#cm_wheel-chart',
      template: 'product/wheelChart',
      count: 50,
      requestExtra: { wheelChart: true },
      useManualItems: true,
    },
    {
      name: 'CartCrossSell',
      type: 'RelatedItems',
      location: { insertAfter: 'body.woocommerce-cart #cm_related-items', class: 'cm_hide' },
      template: 'product/cartCrossSell',
      count: 50,
      onItemsRendered: onCartCrossSellRendered,
      requestExtra: { cartCrossSell: true },
      useManualItems: true,
    },
    {
      name: 'FitmentTable',
      location: '#cm_fitment-table',
      template: 'fitmentSearch/fitmentTable',
    },
    {
      name: 'AutoSyncVisualization',
      apiKey: AutoSyncKey,
      config: {
        height: '150px',
        vehicleImageSize: 640,
        vehicleAngles: [32],
        showColorPicker: false,
      },
      classModificator: 'search-result',
    },
    {
      name: 'FacetDialog',
      template: 'FacetDialog',
      ignoreFields: vehicleLanding ? ['Vehicle'] : [],
    },
    {
      name: 'SearchBoxDialog',
      template: 'SearchBoxDialog',
    },
    {
      name: 'VehicleWidgetDialog',
      template: 'fitmentSearch/dialog',
      isAlwaysColumnLayout: true,
    },
    {
      name: 'AutoSyncVisualizationDialog',
      apiKey: AutoSyncKey,
      template: 'fitmentSearch/autoSyncVisualizationDialog',
    },
  ],
};
